import type { DeviceResponseV5 } from '@emma/api_client/types';
import { IconButton, Menu, Tooltip, useToast } from '@lego/klik-ui';
import { EllipsisSolid } from '@lego/klik-ui/icons';
import type { InferRequestType } from 'hono/client';
import type { MouseEvent } from 'react';
import { useState } from 'react';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { type ApiClient, useMutation } from '@/hooks/api';
import { ToastOptions } from '@/utility/ToastOptions';

type DeviceQuickActionsProps = {
  device: DeviceResponseV5;
  fleetNames: Map<number, string>;
};

type PendingAction = { type: 'reboot' } | { type: 'restart'; fleetId?: number; fleetName?: string };

type PostDeviceAction = ApiClient['v1']['devices'][':deviceId']['actions']['$post'];
type DeviceActionBody = InferRequestType<PostDeviceAction>['json'];

export const DeviceQuickActions = ({ device, fleetNames }: DeviceQuickActionsProps) => {
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const toast = useToast();

  const postAction = useMutation<unknown, DeviceActionBody>(
    `device-quick-action-${device.id}`,
    (client, json) =>
      client.v1.devices[':deviceId'].actions.$post({
        param: { deviceId: device.id.toString() },
        json,
      })
  );

  const isOffline = !device.isConnected;
  const isNotProvisioned = device.state !== 'PROVISIONED';
  const isDisabled = isOffline || isNotProvisioned;
  const disabledTooltip = isNotProvisioned
    ? 'Device has not been provisioned yet.'
    : 'Device is offline';
  const activeDeployments = device.deployments?.filter((d) => d.isActive) ?? [];
  const hasMultipleDeployments = activeDeployments.length > 1;
  const hasNoDeployments = activeDeployments.length === 0;

  const executeAction = async (
    actionBody: DeviceActionBody,
    successTitle: string,
    successMessage: string
  ) => {
    try {
      const result = await postAction(actionBody);
      if (result !== undefined) {
        toast(ToastOptions.success(successTitle, successMessage, 3000));
      }
    } catch {
      // The mutation hook already displays the error toast.
    }
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    setPendingAction(null);

    if (pendingAction.type === 'reboot') {
      await executeAction(
        { action: 'REBOOT' },
        'Reboot scheduled',
        `Device reboot has been queued for ${device.name}.`
      );
    } else if (pendingAction.type === 'restart') {
      const body: { action: 'RESTART-APPLICATION'; fleetId?: number } = {
        action: 'RESTART-APPLICATION',
      };
      if (pendingAction.fleetId !== undefined) {
        body.fleetId = pendingAction.fleetId;
      }
      await executeAction(
        body,
        'Application restart scheduled',
        `Application restart has been queued for ${device.name}.`
      );
    }
  };

  const getConfirmationHeader = (): string => {
    if (!pendingAction) return '';
    if (pendingAction.type === 'reboot') {
      return `Reboot ${device.name}?`;
    }
    const fleetSuffix = pendingAction.fleetName ? ` (${pendingAction.fleetName})` : '';
    return `Restart application on ${device.name}${fleetSuffix}?`;
  };

  const getConfirmationBody = (): string => {
    if (!pendingAction) return '';
    if (pendingAction.type === 'reboot') {
      return 'This will reboot the device. The device will be temporarily unavailable during the reboot. This action cannot be undone.';
    }
    return 'This will restart the application deployment on the device. Running containers will be stopped and restarted. This action cannot be undone.';
  };

  return (
    <>
      <Menu isLazy placement="top">
        <Menu.Button
          aria-label="Device actions"
          as={IconButton}
          icon={<EllipsisSolid />}
          onClick={(e: MouseEvent) => e.stopPropagation()}
          size="xs"
          variant="ghost"
        />
        <Menu.List onClick={(e: MouseEvent) => e.stopPropagation()}>
          {hasMultipleDeployments ? (
            <Menu.Group title="Restart Application">
              {activeDeployments.map((deployment) => {
                const name = fleetNames.get(deployment.fleetId) ?? `Fleet ${deployment.fleetId}`;
                return (
                  <Tooltip
                    isDisabled={!isDisabled}
                    key={deployment.fleetId}
                    label={disabledTooltip}
                  >
                    <Menu.Item
                      isDisabled={isDisabled}
                      onClick={() =>
                        setPendingAction({
                          type: 'restart',
                          fleetId: deployment.fleetId,
                          fleetName: name,
                        })
                      }
                    >
                      {name}
                    </Menu.Item>
                  </Tooltip>
                );
              })}
            </Menu.Group>
          ) : (
            <Tooltip
              isDisabled={!isDisabled && !hasNoDeployments}
              label={isDisabled ? disabledTooltip : 'No active deployments'}
            >
              <Menu.Item
                isDisabled={isDisabled || hasNoDeployments}
                onClick={() => setPendingAction({ type: 'restart' })}
              >
                Restart Application
              </Menu.Item>
            </Tooltip>
          )}
          <Tooltip isDisabled={!isDisabled} label={disabledTooltip}>
            <Menu.Item isDisabled={isDisabled} onClick={() => setPendingAction({ type: 'reboot' })}>
              Reboot Device
            </Menu.Item>
          </Tooltip>
        </Menu.List>
      </Menu>

      <ConfirmationDialog
        body={getConfirmationBody()}
        header={getConfirmationHeader()}
        isOpen={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        onConfirmed={handleConfirm}
      />
    </>
  );
};
